import { Extension, getSplittedAttributes } from '@tiptap/core';
import { Fragment } from '@tiptap/pm/model';
import { TextSelection } from '@tiptap/pm/state';
import { getFromPath, isDirectParentActive, getSpecialNodeTypes } from '../utils.js';

const listTypeNames = [
  "fbcNumberList",
  "fbcBulletList",
  "fbcCheckList",
  "fbcToggleList"
];
const FbcList = Extension.create({
  name: "fbcList",
  addCommands() {
    return {
      splitList: () => ({ chain, editor, tr }) => {
        const { $from, from, to } = editor.state.selection;
        const parent = $from.parent;
        const parentPos = $from.start();
        const listParent = $from.node($from.depth - 1);
        const listParentPos = $from.start($from.depth - 1);
        const extensionAttributes = editor.extensionManager.attributes;
        if ($from.depth === 0 || !listTypeNames.includes(listParent.type.name) || listParent.firstChild !== parent) {
          return false;
        }
        const simpleSelection = from === to;
        const hasMultipleChildren = listParent.childCount > 1;
        const { toggleList } = getSpecialNodeTypes(editor.state.schema);
        if (listParent.childCount === 1 && parent.content.size === 0) {
          return chain().lift(listParent.type.name).run();
        }
        const atStart = from === parentPos;
        const atEnd = from === parentPos + parent.content.size || to === parentPos + parent.content.size;
        const atEndSimple = !hasMultipleChildren && atEnd;
        if (toggleList && listParent.type === toggleList && listParent.attrs.expanded === true && atEnd) {
          return false;
        }
        if (!simpleSelection) {
          tr.deleteSelection();
        }
        const newAttributes = {
          ...getSplittedAttributes(extensionAttributes, listParent.type.name, listParent.attrs)
        };
        if (atStart || atEndSimple) {
          const newList2 = listParent.type.createAndFill(newAttributes, []);
          if (!newList2) {
            return false;
          }
          const newListPos2 = tr.mapping.map(atStart ? listParentPos - 1 : listParentPos + listParent.nodeSize - 1);
          if (atEndSimple) tr.setSelection(TextSelection.create(tr.doc, newListPos2 - 1));
          tr.insert(newListPos2, newList2);
          return true;
        }
        const newParentAttributes = {
          ...getSplittedAttributes(extensionAttributes, parent.type.name, parent.attrs)
        };
        const newParent = parent.type.createAndFill(newParentAttributes, parent.content.cut(from - parentPos));
        if (!newParent) {
          return false;
        }
        const restOfListChildren = listParent.content.cut(parentPos + parent.nodeSize - 1 - listParentPos);
        const newListContent = Fragment.from([
          newParent
        ]).append(restOfListChildren);
        const newList = listParent.type.createAndFill(newAttributes, newListContent);
        if (!newList) {
          return false;
        }
        tr.delete(from, listParentPos + listParent.content.size);
        const newListPos = tr.mapping.map(listParentPos + listParent.nodeSize - 1);
        tr.insert(newListPos, newList);
        tr.setSelection(TextSelection.create(tr.doc, newListPos + 1));
        return true;
      },
      fbcToggleList: (listType) => ({ editor, state, tr }) => {
        const listNodeType = state.schema.nodes[listType];
        const path = getFromPath(state.selection);
        if (!path || path.length <= 1) return false;
        const parent = path[0];
        const grandParent = path[1];
        const isTargetListTypeActive = isDirectParentActive(editor, listType);
        if (isTargetListTypeActive) {
          if (!grandParent) return false;
          tr.replaceWith(grandParent.pos, grandParent.end, grandParent.node.content);
          tr.setSelection(TextSelection.create(tr.doc, state.selection.from - 1));
          return true;
        }
        if (grandParent && grandParent.node.type.isInGroup("list") && parent.index === 0) {
          const newList2 = listNodeType.createAndFill(null, grandParent.node.content);
          if (!newList2) return false;
          tr.replaceWith(grandParent.pos, grandParent.end, newList2);
          tr.setSelection(TextSelection.create(tr.doc, state.selection.from));
          return true;
        }
        const canBecomeDirectParent = listNodeType.contentMatch.matchType(parent.node.type);
        const canBecomeDirectChild = parent.node.type.contentMatch.matchType(listNodeType);
        if (canBecomeDirectChild && !canBecomeDirectParent) {
          const newList2 = listNodeType.createAndFill();
          if (!newList2) return false;
          tr.insert(parent.end - 1, newList2);
          tr.setSelection(TextSelection.create(tr.doc, parent.end));
          return true;
        }
        const parentContentCopy = Fragment.from(parent.node.copy(parent.node.content));
        const content = canBecomeDirectParent ? parentContentCopy : Fragment.from(state.schema.nodes.paragraph.createAndFill(null, [])).append(parentContentCopy);
        const newList = listNodeType.createAndFill(null, content);
        if (!newList) return false;
        tr.replaceWith(parent.pos, parent.end, newList);
        tr.setSelection(TextSelection.create(tr.doc, state.selection.from));
        return true;
      }
    };
  },
  addKeyboardShortcuts() {
    return {
      Enter: ({ editor }) => {
        return editor.commands.splitList();
      },
      Backspace: ({ editor }) => {
        const { $from, from, to } = editor.state.selection;
        if (from !== to || $from.depth === 0) return false;
        const parent = $from.parent;
        const parentPos = $from.start();
        const listParent = $from.node($from.depth - 1);
        if (!listTypeNames.includes(listParent.type.name) || listParent.firstChild !== parent) return false;
        if (from !== parentPos) return false;
        return editor.commands.lift(listParent.type.name);
      }
    };
  }
});

export { FbcList as default };
//# sourceMappingURL=FbcList.js.map
